import { AnyAction, combineReducers } from 'redux';
import { createMigrate, persistReducer, purgeStoredState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { v4 as uuid } from 'uuid';
// slices
import campaignReducer, {
  campaignPersistWhitelist,
  initialState as campaignInitialState
} from './slices/campaign';
import companyReducer from './slices/company';
import dashboardReducer, {
  dashboardPersistWhitelist,
  initialState as dashboardInitialState
} from './slices/dashboard';

import dataReducer from './slices/data';
import datagridReducer, {
  migrations as datagridMigrations,
  persistsVersion as datagridPersistVersion
} from './slices/datagrid';
import employeeReducer from './slices/employee';
import organizationReducer from './slices/organization';
import userReducer from './slices/user';
import brandedLinkReducer from './slices/brandedLink';
import apiKeyReducer from './slices/apiKey';
import pgapReducer from './slices/pgap';
import reportsReducer, {
  reportsPersistWhitelist,
  initialState as reportsInitialState
} from './slices/reports';
import respondentReducer from './slices/respondent';
import responseReducer from './slices/responses';
import reviewsReducer from './slices/reviews';
import ivrReducer from './slices/ivr';
import etrack1Reducer from './slices/etrack1';
import integrationsReducer from './slices/integrations';
import versioningReducer from './slices/versioning';
import releaseConfig from 'releaseConfig';
import providersReducer from './slices/providers';
import permissionsReducer from './slices/permissions';
import campaignMemberReducer from './slices/campaignMember';
import throttlingGroupReducer from './slices/throttlingGroup';
import editingReducer from './slices/editing';
import relayReducer from './slices/relay';
import orderReducer from './slices/orders';
import orderTypesReducer from './slices/orderTypes';
import tenantOrdersReducer from './slices/tenantOrders';
import billingConfigurationReducer from './slices/billingConfiguration';
import npsNotificationsReducer from './slices/npsNotifications';
import mergeFieldsReducer from './slices/mergeFields';
import productConfigurationsReducer from './slices/productConfigurations';
import aiFeedbackConfigurationReducer from './slices/aiFeedbackConfiguration';
import externalAuthenticationMethodsReducer from './slices/externalAuthentication';

import { getPersistConfig } from 'redux-deep-persist';
import { isDev } from 'authConfig';

import aiFeedbackReducer from './slices/aiFeedback';
import aiTokenUsageReducer from './slices/aiTokenUsage';

// ----------------------------------------------------------------------

const rootPersistConfig = {
  key: 'root',
  storage,
  keyPrefix: 'redux-',
  whitelist: []
};

const campaignPersistConfig = getPersistConfig({
  key: 'campaign',
  storage,
  keyPrefix: 'redux-',
  whitelist: campaignPersistWhitelist,
  rootReducer: campaignReducer
});

const dashboardPersistConfig = getPersistConfig({
  key: 'dashboard',
  storage,
  keyPrefix: 'redux-',
  whitelist: dashboardPersistWhitelist,
  rootReducer: dashboardReducer
});

const aiFeedbackPersistConfig = getPersistConfig({
  key: 'aiFeedback',
  storage,
  keyPrefix: 'redux-',
  rootReducer: aiFeedbackReducer
});

const datagridPersistConfig = getPersistConfig({
  key: 'datagrid',
  storage,
  keyPrefix: 'redux-',
  rootReducer: datagridReducer,
  version: datagridPersistVersion,
  migrate: createMigrate(datagridMigrations, { debug: isDev })
});

const reportsPersistConfig = getPersistConfig({
  key: 'reports',
  storage,
  keyPrefix: 'redux-',
  whitelist: reportsPersistWhitelist,
  rootReducer: reportsReducer
});

const versioningPersistConfig = {
  key: 'versioning',
  storage,
  keyPrefix: 'redux-',
  rootReducer: versioningReducer
};

const appReducer = combineReducers({
  campaign: persistReducer(campaignPersistConfig, campaignReducer),
  response: responseReducer,
  pgap: pgapReducer,
  employee: employeeReducer,
  organization: organizationReducer,
  user: userReducer,
  brandedLink: brandedLinkReducer,
  apiKey: apiKeyReducer,
  company: companyReducer,
  datagrid: persistReducer(datagridPersistConfig, datagridReducer),
  respondent: respondentReducer,
  dashboard: persistReducer(dashboardPersistConfig, dashboardReducer),
  data: dataReducer,
  reports: persistReducer(reportsPersistConfig, reportsReducer),
  reviews: reviewsReducer,
  ivr: ivrReducer,
  etrack1: etrack1Reducer,
  integrations: integrationsReducer,
  versioning: persistReducer(versioningPersistConfig, versioningReducer),
  providers: providersReducer,
  permissions: permissionsReducer,
  campaignMember: campaignMemberReducer,
  throttlingGroup: throttlingGroupReducer,
  editing: editingReducer,
  relay: relayReducer,
  order: orderReducer,
  orderTypes: orderTypesReducer,
  tenantOrders: tenantOrdersReducer,
  billingConfiguration: billingConfigurationReducer,
  npsNotifications: npsNotificationsReducer,
  mergeFields: mergeFieldsReducer,
  productConfigurations: productConfigurationsReducer,
  aiFeedbackConfiguration: aiFeedbackConfigurationReducer,
  externalAuthenticationMethods: externalAuthenticationMethodsReducer,
  aiFeedback: persistReducer(aiFeedbackPersistConfig, aiFeedbackReducer),
  aiTokenUsage: aiTokenUsageReducer
});

export enum RootReducerTypes {
  RESET_STATES = 'RESET_STATES',
  REFRESH_ON_NEW_VERSION = 'REFRESH_ON_NEW_VERSION',
  LOGOUT = 'LOGOUT'
}

export type AppRootState = ReturnType<typeof appReducer>;

const purgePersistedUserData = () => purgeStoredState(aiFeedbackPersistConfig);

const rootReducer = (state: AppRootState | undefined, action: AnyAction) => {
  if (action.type === RootReducerTypes.LOGOUT && state) {
    state = getLoggedOutStates(state);
  }
  if (action.type === RootReducerTypes.RESET_STATES) {
    state = resetAndGetStates(state) as AppRootState;
  }
  if (action.type === RootReducerTypes.REFRESH_ON_NEW_VERSION) {
    state = resetAndGetStates(state) as AppRootState;

    console.info(
      '%cAutomatically refreshing page due to new version of build',
      'font-weight: bold; font-size: 1.5rem;color: red; text-shadow: 1px 1px 0 black'
    );

    //save buildNumber being used on refresh in session storage, as redux states may not be updated correctly/yet in all instances
    sessionStorage.setItem('latestBuildNumber', releaseConfig.frontendBuildNumber);

    //do a hard refresh of page
    const currentHref = globalThis.location.href;
    globalThis.location.href = currentHref;
  }

  return appReducer(state, action);
};

export { rootPersistConfig, rootReducer, purgePersistedUserData };

const getLoggedOutStates = (currentState: AppRootState): AppRootState => {
  const initialStates = appReducer(undefined, { type: RootReducerTypes.LOGOUT });
  const { versioning, datagrid } = currentState;

  return {
    ...initialStates,
    campaign: { ...initialStates.campaign, _persist: currentState.campaign._persist },
    dashboard: { ...initialStates.dashboard, _persist: currentState.dashboard._persist },
    reports: { ...initialStates.reports, _persist: currentState.reports._persist },
    aiFeedback: {
      ...initialStates.aiFeedback,
      conversationId: uuid(),
      _persist: currentState.aiFeedback._persist
    },
    versioning,
    datagrid
  };
};

type GetResetPartlyPersistedStateProps = {
  currentState: Record<string, any>;
  initialState: Record<string, any>;
  persistedAttributes: string[];
};
const getResetPartlyPersistedState = ({
  currentState,
  initialState,
  persistedAttributes
}: GetResetPartlyPersistedStateProps) => {
  let newState = { ...initialState }; // Start from initialState

  // Only overwrite properties that are in persistedAttributes
  for (let attr of persistedAttributes) {
    if (currentState.hasOwnProperty(attr)) {
      newState[attr] = currentState[attr];
    }
  }
  return newState;
};

const resetAndGetStates = (currentState: any) => {
  // Reset partly persisted states, only keeping the whitelisted attributes
  const partlyPersistedInitialStates: {
    [key: string]: Record<string, any>;
  } = {
    dashboard: getResetPartlyPersistedState({
      currentState: currentState.dashboard,
      initialState: dashboardInitialState,
      persistedAttributes: dashboardPersistWhitelist
    }),
    campaign: getResetPartlyPersistedState({
      currentState: currentState.campaign,
      initialState: campaignInitialState,
      persistedAttributes: campaignPersistWhitelist
    }),
    reports: getResetPartlyPersistedState({
      currentState: currentState.reports,
      initialState: reportsInitialState,
      persistedAttributes: reportsPersistWhitelist
    })
  };

  const { versioning, datagrid } = currentState; // Everything in these states should be kept
  return { versioning, datagrid, ...partlyPersistedInitialStates };
};
